/** @format */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { configDefault } from '../../core/config';
import { useDispatch } from 'react-redux';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import PaperedTable from '../../components/Table/PaperedTable';
import { TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import EditButton from '../../components/Buttons/EditButton';
import I18n from '../../components/Translate/I18n';
import Pager from '../../components/Adescom/Pager';
import { tariffConstants } from '../../modules/tariffs/actions';
import { makeStyles } from '@material-ui/core/styles';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import { DateHelperTs } from '../../helpers/DateHelperTs';
import Loader from '../../components/Loaders/Loader';
import CancelButton from '../../components/Buttons/CancelButton';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import BasePricelistInterface, { BasePricelistCollectionInterface } from '../../interfaces/billing/BasePricelistInterface';
import TariffInterface from '../../interfaces/billing/TariffInterface';
import Paper from '@material-ui/core/Paper';
import Card from '../../components/Card/Card';
import { LinearBillingEdit } from './LinearBillingEdit';
import PricelistInterface from '../../interfaces/billing/PricelistInterface';
import DeleteButton from '../../components/Buttons/DeleteButton';
import { AlertDialog } from '../lib/dialog/AlertDialog';
import { ImportList } from './Import/ImportList';
import {FilterReducerFieldName} from "../../modules/filter/FilterReducers";
import {FilterListReducerFieldName} from "../../modules/filter/FilterListReducers";

const useStyle = makeStyles(adescomStyle);

interface LinearBillingListProps {
    tariff: TariffInterface;
}

export const LinearBillingList = (props: LinearBillingListProps): React.ReactElement => {
    const classes = useStyle();

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const [importOpen, setImportOpen] = useState(false);

    const closeImport = useCallback((): void => {
        setImportOpen(false);
    }, []);

    const dispatch = useDispatch();
    const itemLoader = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);

    const [loading, setLoading] = useState<boolean>(false);
    const [linearBillingDialogOpen, setLinearBillingDialogOpen] = useState<boolean>(false);
    const [selectedPricelist, setSelectedPricelist] = useState<BasePricelistInterface | null>(null);

    const [deleteAlertOpen, setDeleteAlertOpen] = useState<boolean>(false);
    const [pendingDeletePricelist, setPendingDeletePricelist] = useState<BasePricelistInterface | null>(null);

    const tariff = useMemo((): TariffInterface => {
        return props.tariff;
    }, [props.tariff]);

    const [priceLists, setPriceLists] = useState<BasePricelistCollectionInterface | null>(null);

    const [defaultFilters, setDefaultFilters] = useState<any>([{ name: 'tariffId', value: tariff.id }]);
    const [filters, setFilters] = useState<any>([]);
    const [orders, setOrders] = useState<any>([]);

    const getPricelists = useCallback((): void => {
        const tmpFilters = [...defaultFilters, ...filters];
        const tmpOrders = [...orders];

        setLoading(true);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/pricelists', configDefault.PLATFORM_API_PREFIX)),
            'PRICELIST_LIST',
            tmpFilters,
            tmpOrders,
            {
                onSuccess: (data: any) => {
                    setLoading(false);
                    setPriceLists(data);
                },
                onError: () => {
                    setLoading(false);
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [itemLoader, defaultFilters, filters, orders]);

    useEffect(() => {
        getPricelists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const paginationCallbackFunction = useCallback(
        (uri: string): void => {
            setLoading(true);
            const tmpFilters = [...defaultFilters, ...filters];
            const tmpOrders = [...orders];

            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                tariffConstants.LIST,
                tmpFilters,
                tmpOrders,
                {
                    onSuccess: (data: any) => {
                        setLoading(false);
                        setPriceLists(data);
                    },
                    onError: () => {
                        setLoading(false);
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [itemLoader, filters, defaultFilters, orders]
    );

    const addLinearBilling = useCallback((param: any): void => {
        setSelectedPricelist(null);
        setLinearBillingDialogOpen(true);
    }, []);

    const editLinearBilling = useCallback((param: any): void => {
        setLinearBillingDialogOpen(true);
        setSelectedPricelist(param.pricelist);
    }, []);

    const deleteLinearBilling = useCallback((param: any): void => {
        setDeleteAlertOpen(true);
        setPendingDeletePricelist(param.pricelist);
    }, []);

    const deleteLinearBillingConfirm = useCallback((): void => {
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_billing_actions')),
            'delete_linear_billing',
            {
                action: 'delete_linear_billing',
                params: JSON.stringify({
                    pricelist: pendingDeletePricelist,
                    tariff: tariff,
                }),
            },
            {
                onSuccess: () => {
                    setPendingDeletePricelist(null);
                    setDeleteAlertOpen(false);
                    if (!priceLists) {
                        getPricelists();
                    } else {
                        paginationCallbackFunction(priceLists['hydra:view']['@id']);
                    }
                },
                onError: () => {
                    //
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: true,
            }
        );
    }, [itemLoader, pendingDeletePricelist, tariff, priceLists, getPricelists, paginationCallbackFunction]);

    const closeLinearBillingDialog = useCallback(
        (reloadData = false): void => {
            setLinearBillingDialogOpen(false);
            if (reloadData) {
                if (!priceLists) {
                    getPricelists();
                } else {
                    paginationCallbackFunction(priceLists['hydra:view']['@id']);
                }
            }
        },
        [paginationCallbackFunction, priceLists, getPricelists]
    );

    const updateFilters = useCallback(
        (newFilters: any): void => {
            setFilters(newFilters);
        },
        [setFilters]
    );

    const updateOrders = useCallback(
        (newOrders: any): void => {
            setOrders(newOrders);
        },
        [setOrders]
    );

    const clearFilters = useCallback(() => {
        updateFilters([]);
        updateOrders([]);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    }, [dispatch, updateOrders, updateFilters]);

    const typeSelect: any = useMemo((): any => {
        return [
            { name: 'Połączenie głosowe', value: 0 },
            { name: 'Usługa', value: 1 },
        ];
    }, []);

    const exportPricelists = useCallback((): void => {
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_billing_actions')),
            'export_linear_billing',
            {
                action: 'export_linear_billing',
                params: JSON.stringify({
                    tariff: tariff,
                }),
            },
            {
                onSuccess: (data: any) => {
                    //Download csv file
                    const element = document.createElement('a');
                    const file = new Blob([data], { type: 'text/csv' });
                    element.href = URL.createObjectURL(file);
                    // element.download = 'export.csv';
                    document.body.appendChild(element); // Required for this to work in FireFox
                    element.click();
                },
                onError: () => {
                    //
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: true,
            }
        );
    }, [itemLoader, tariff]);

    return (
        <>
            <AlertDialog
                open={deleteAlertOpen}
                titleDialog={'pricelists:b.delete_pricelist_element'}
                displayMessage={'pricelists:lb.delete_confirm'}
                closeCallback={(): void => {
                    setDeleteAlertOpen(false);
                    setPendingDeletePricelist(null);
                }}
                confirmCallback={deleteLinearBillingConfirm}
            />
            <Paper>
                <Card>
                    <GridContainer>
                        <GridItem xs={2}>
                            <h3>
                                <I18n trans={'pricelists:lb.pricelists'} />
                            </h3>
                        </GridItem>
                        <GridItem xs={10}>
                            {UserRoleHelper.checkRole(roles.ROLE_UPLOAD_LINEAR_BILLING) && (
                                <CustomTextButtom
                                    size={'lg'}
                                    color="primary"
                                    label={'pricelists:lb.import'}
                                    callback={(): void => {
                                        setImportOpen(true);
                                    }}
                                    callbackParam={{}}
                                />
                            )}
                            {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_POST) && (
                                <CustomTextButtom
                                    size={'lg'}
                                    color="primary"
                                    label={'pricelists:lb.export'}
                                    callback={exportPricelists}
                                    callbackParam={{}}
                                />
                            )}
                            {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_POST) && (
                                <CustomTextButtom
                                    size={'lg'}
                                    color="primary"
                                    label={'pricelists:lb.add_pricelist'}
                                    callback={addLinearBilling}
                                    callbackParam={{}}
                                />
                            )}
                        </GridItem>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={getPricelists}
                                    callbackParam={null}
                                    size={'lg'}
                                    color="primary"
                                    label="general:common.search"
                                />
                            </GridItem>
                        <GridItem xs={12}>
                            <PaperedTable>
                                <TableHead>
                                    <TableRow>
                                        <TableCell classes={tableStickyCommand} padding="none" align="left">
                                            <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                            {(orders.length > 0 || filters.length > 0) && (
                                                <CancelButton
                                                    callback={clearFilters}
                                                    callbackParam={{}}
                                                    tooltipTitle="general:common.clear_filter"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pricelists:lb.name"
                                                updateOrderList={updateOrders}
                                                updateFilterList={updateFilters}
                                                fieldName="mainname"
                                                showSort={true}
                                                defaultDirection="desc"
                                                searchFieldType="string"
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pricelists:lb.activedate"
                                                updateOrderList={updateOrders}
                                                updateFilterList={updateFilters}
                                                fieldName="activeDate"
                                                showSort={false}
                                                defaultDirection="desc"
                                                searchFieldType={null}
                                                dateFormat="DD-MM-RRRR"
                                                beforeSetToMax={false}
                                                afterSetToMin={false}
                                                dateFromTo={false}
                                                breakLine={false}
                                                timeFormat={false}
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pricelists:lb.updatedAt"
                                                updateOrderList={updateOrders}
                                                updateFilterList={updateFilters}
                                                fieldName="updatedAt"
                                                showSort={false}
                                                defaultDirection="desc"
                                                searchFieldType={null}
                                                dateFormat="DD-MM-RRRR"
                                                beforeSetToMax={false}
                                                afterSetToMin={false}
                                                dateFromTo={false}
                                                breakLine={false}
                                                timeFormat={false}
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <Loader visible={loading} />
                                    {priceLists &&
                                        priceLists['hydra:member'] &&
                                        priceLists['hydra:member'].map((pricelist: BasePricelistInterface) => {
                                            let latestActiveDate = pricelist.activeDate;
                                            if ({}.propertyIsEnumerable.call(pricelist, 'latestActiveDate')) {
                                                const typeOverwrite = (pricelist as unknown) as PricelistInterface;
                                                latestActiveDate = typeOverwrite.latestActiveDate;
                                            }

                                            return (
                                                <TableRow key={pricelist.customId}>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_PUT) && (
                                                            <EditButton
                                                                tooltipTitle="pricelists:b.edit_pricelist_element"
                                                                callback={editLinearBilling}
                                                                callbackParam={{ pricelist: pricelist }}
                                                            />
                                                        )}
                                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_CUSTOM_BILLING_ACTION_PRICELIST_REMOVE) && (
                                                            <DeleteButton
                                                                tooltipTitle="pricelists:b.delete_pricelist_element"
                                                                callback={deleteLinearBilling}
                                                                callbackParam={{ pricelist: pricelist }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {pricelist.mainname}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {pricelist.activeDate && DateHelperTs.format(pricelist.activeDate, 'DD-MM-YYYY')}
                                                        {latestActiveDate &&
                                                            pricelist.activeDate !== latestActiveDate &&
                                                            DateHelperTs.format(latestActiveDate, 'DD-MM-YYYY')}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {pricelist.latestUpdatedAt && DateHelperTs.format(pricelist.latestUpdatedAt, 'DD-MM-YYYY')}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell classes={tableCellRoot} colSpan={5}>
                                            {priceLists && priceLists['hydra:view'] && (
                                                <Pager callback={paginationCallbackFunction} views={priceLists['hydra:view']} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </PaperedTable>
                        </GridItem>
                    </GridContainer>
                </Card>
            </Paper>
            {linearBillingDialogOpen && (
                <LinearBillingEdit
                    tariff={tariff}
                    selectedPricelist={selectedPricelist}
                    open={linearBillingDialogOpen}
                    closeDialog={closeLinearBillingDialog}
                />
            )}
            {importOpen && <ImportList open={importOpen} tariff={tariff} closeDialog={closeImport} />}
        </>
    );
};
